// Account security (PB-17-08, PD-020): turn two-step verification on/off and replace recovery codes.
import type { MfaRecoveryCodesResponse } from '@passage/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiRequestError, api } from '../api.ts';
import { MfaEnrol, RecoveryCodes } from '../components/Mfa.tsx';
import { ErrorSummary, Field, PageHead } from '../components/ui.tsx';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';

export function AccountSecurity({ base, enrolled, required, onChanged }: { base: string; enrolled: boolean; required: boolean; onChanged: () => void }) {
  const { t } = useI18n();
  const [enrolling, setEnrolling] = useState(false);
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  const [code, setCode] = useState('');
  const disable = useMutation({ mutationFn: () => api('POST', `${base}/me/mfa/disable`, { code }), onSuccess: () => { setCode(''); onChanged(); } });
  const regen = useMutation({ mutationFn: () => api<MfaRecoveryCodesResponse>('POST', `${base}/me/mfa/recovery-codes`, { code }), onSuccess: (r) => { setCode(''); setCodes(r.recoveryCodes); } });
  const err = disable.error ?? regen.error;
  return (
    <>
      <PageHead eyebrow="Passage" title={t('mfa.account.title')} />
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>{t('mfa.title')}</h2>
            <p>{enrolled ? t('mfa.account.status.on') : t('mfa.account.status.off')}</p>
            {required ? <p>{t('mfa.account.required')}</p> : null}
          </div>
          <span className={`badge ${enrolled ? 'green' : 'amber'}`}>{enrolled ? t('status.active') : t('status.inactive')}</span>
        </div>
        <div className="pad">
          {codes ? (
            <RecoveryCodes
              codes={codes}
              onDone={() => {
                setCodes(null);
                onChanged();
              }}
            />
          ) : enrolling ? (
            <MfaEnrol<MfaRecoveryCodesResponse>
              base={base}
              required={required}
              onDone={(r) => {
                setEnrolling(false);
                setCodes(r.recoveryCodes);
              }}
            />
          ) : !enrolled ? (
            <button className="button primary" type="button" onClick={() => setEnrolling(true)}>
              {t('mfa.account.turnOn')}
            </button>
          ) : (
            <div className="stack" style={{ maxInlineSize: 420 }}>
              <ErrorSummary error={err} fieldIds={{ code: 'mfa-confirm' }} />
              <Field id="mfa-confirm" label={t('mfa.account.confirmCode')} error={err instanceof ApiRequestError ? err.fields.code : undefined}>
                {(p) => <input {...p} dir="ltr" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />}
              </Field>
              <div className="actions">
                <button className="button" type="button" disabled={code.length !== 6 || regen.isPending} onClick={() => regen.mutate()}>
                  {t('mfa.account.newCodes')}
                </button>
                {base.includes('platform') ? null : (
                  <button className="button danger" type="button" disabled={required || code.length !== 6 || disable.isPending} onClick={() => disable.mutate()}>
                    {t('mfa.account.turnOff')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function OrgAccountPage() {
  const { session, refresh } = useSession();
  return <AccountSecurity base="/api/v1" enrolled={session.mfa.enrolled} required={session.policy.mfaRequired} onChanged={() => void refresh()} />;
}
